/* eslint-disable @typescript-eslint/naming-convention */
import { Post } from 'config/apiServiceConfig'
import axiosInstance from 'config/apiServiceApiGateway'

export type GetInventoryLocationParamsType = Partial<{
  location_id: number
  product_ids: number
  rack_name_contains: string
  have_stock: boolean
  status: '' | number
  package_label_contains: string
  package_label_exact: string
  package_id: number
  'pagination.sort': number
  'pagination.direction': 'asc' | 'desc'
  'pagination.page_index': number
  'pagination.page_size': number
}>

export type GetInventoryLocationResponseType = {
  data: {
    id: number
    qr_code: string
    stock: number
    created_at: string
    updated_at: string
    created_by: string
    updated_by?: string
    is_active: boolean
    status: number
    status_name: string
    status_notes_id?: number
    status_note_name?: string
    expiry_date?: number
    product: {
      id: number
      name: string
      sku_number: string
      product_age?: number
    }
    location: {
      id: number
      name: string
    }
    rack?: {
      id: number
      location_id: number
      rack_name: string
      area_id?: number
      area_name?: string
      is_active: boolean
      storage_type_id: number
      storage_type_name: string
      storage_environment_id: number
      storage_environment_name: string
    }
    package_id?: {
      id: number
      package_label: string
      package_type: string
    }
  }[]
  pagination: {
    sort: string
    direction: 'asc' | 'desc'
    page_index: number
    page_size: number
  }
}

export type PostInventoryLocationPayloadType = {
  isActive: boolean
  locationId: string
  productId: string
  rackId: number
  statusId: number
  stock: string
  expiryDate?: number
  packageId?: number
  statusNotesId?: number
}

export type PostInventoryTransferByPackageIdValidatePayloadType = {
  origin_inventory_detail_ids: number[]
  destination_rack: string
}

export type PostInventoryTransferByPackageIdValidateResponseType = {
  data: {
    eligible: boolean
    rack_id: number
    rack_name: string
  }
  error: null
}

export type PostInventoryTransferByAssetPayloadType = {
  location_id: number
  asset_id: number
  to_rack_id: number
}

export type PostInventoryTransferByAssetResponseType = {
  data: {
    message: string
  }
  error: null
}

export type PostInventoryTransferBySkuPayloadType = {
  inventory_detail_id: number
  to_package_id: number
  to_rack_id: number
  qty: number
  is_change_exp_date?: boolean
  to_exp_date?: string
  is_change_status?: boolean
  to_status?: number
  to_status_note?: number
}

export type PostInventoryTransferBySkuResponseType = {
  data: {
    message: string
  }
  error: null
}

export type GetEligiblePackageIdParamsType = {
  origin_inventory_detail_id: number
}

export type GetEligiblePackageIdResponseType = {
  data: {
    id: number
    package_label: string
    type: string
  }[]
  error: null
}

export type GetEligibleAssetsParamsType = Partial<{
  'pagination.page': number
  inventory_detail_id: number
  asset_code: string
  to_status: number
  to_exp_date: string
}>

export type GetEligibleAssetsResponseType = {
  data: {
    id: number
    code: string
  }[]
  error: {
    message: string
    status: boolean
    code: number
  }
  pagination: {
    page_size: number
    sort: string
    direction: string
    page_index: number
  }
}

export type GetEligibleRackParamsType = Partial<{
  'pagination.page': number
  inventory_detail_id: number
  package_id: number
  rack_name: string
  is_transfer_by_asset: boolean
  to_status: number
}>

export type GetEligibleRackResponseType = {
  data: {
    id: number
    name: string
  }[]
  error: null
}

export const getInventoryLocation = (params?: GetInventoryLocationParamsType) =>
  axiosInstance.Get<GetInventoryLocationResponseType, true>({
    url: `/wims/internal/v1/inventories/detail`,
    params,
  })

export const postInventoryLocation = (payload: PostInventoryLocationPayloadType) =>
  Post({
    url: `/api/ims/v2/inventories/detail`,
    data: payload,
  })

export const postInventoryTransferByAsset = (payload: PostInventoryTransferByAssetPayloadType) =>
  axiosInstance.Post<PostInventoryTransferByAssetResponseType>({
    url: `/wims/internal/v1/inventories/transfer/warehouse/by-asset`,
    data: payload,
  })

export const postInventoryTransferBySku = (payload: PostInventoryTransferBySkuPayloadType) =>
  axiosInstance.Post<PostInventoryTransferBySkuResponseType>({
    url: `/wims/internal/v1/inventories/transfer/warehouse/by-sku`,
    data: payload,
  })

export const getEligiblePackageId = (params: GetEligiblePackageIdParamsType) =>
  axiosInstance.Get<GetEligiblePackageIdResponseType, true>({
    url: `/wims/internal/v1/transfer/eligible-package-id`,
    params,
  })

export const getEligibleAssets = (params: GetEligibleAssetsParamsType) =>
  axiosInstance.Get<GetEligibleAssetsResponseType, true>({
    url: `/wims/internal/v1/inventories/transfer/eligible-assets`,
    params,
  })

export const getEligibleRack = (params: GetEligibleRackParamsType) =>
  axiosInstance.Get<GetEligibleRackResponseType, true>({
    url: `/wims/internal/v1/racks/eligible-for-inventory-transfer`,
    params,
  })

export type PostInventoryDetailTransferPayloadType = {
  inventory_detail_id: number
  stock: number
  status_id: number
  status_note_id?: number
  rack_id?: number
  package_id?: number
  expiry_date?: string
  inventory_adjustment_reason_id?: number
}

export type PostInventoryDetailTransferResponseType = {
  message: string
}

export const postInventoryDetailTransfer = (payload: PostInventoryDetailTransferPayloadType) =>
  axiosInstance.Post<PostInventoryDetailTransferResponseType>({
    url: `/wims/internal/v1/inventory-details:transfer`,
    data: payload,
  })
